/**
 * @fileoverview Mock backend for the Sales CRM My Page read endpoints.
 * @description Registers GET /sales/targets/summary on the feature's shared mock adapter and
 * exports the replies to the My Page list (GET /sales/targets) and to Get Target Details (GET
 * /sales/targets/{targetId}), answering in the backend's envelopes with its error codes (Confluence 3.5.11.15-.17, decision
 * record docs/decisions/2026-10-31-my-page.md in the backend repo). Nothing here keeps a second copy
 * of the targets: each answer is projected on the spot from the shared Target, Tenant and roster
 * stores, so a target changed through any mock-only row action shows up here too. Follows the
 * backend's rules: a Super Admin sees every target and anyone else only their own (M6); open
 * targets and, unless `includeClosed=false`, closed (Lost) ones are listed while completed ones
 * are not (M5); the display status is derived in India time with first-match-wins precedence (M4);
 * the list sorts by due date, then priority High to Low, then bank name; the summary counts each
 * visible target once, in the April to March window plus any older target still open. The
 * backend's write-side fields come from the commands in my-page-actions-mock.service.ts: the
 * allowed actions follow the backend's rules for the caller, and a pending reassignment, the time
 * payment details went out and the logged history entries live in my-page-mock-state.ts. Must load before targets-mock.service.ts, whose
 * GET /sales/targets/:id regex would otherwise answer the summary route; that file registers the
 * other two routes here because they share a URL with its own, handing the My Page list (the
 * requests that carry `page`) to myPageListReply and every GET /sales/targets/:id to
 * myPageDetailsReply. A no-op when VITE_ENABLE_MOCK_API is false.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import {
  loggedEventsOf,
  pendingReassignmentOf,
  recordedPaymentSentAt,
  revisionOf,
} from './my-page-mock-state'
import { mockCaller } from './sales-crm-mock-projection'
import { failReply, okEnvelope, pageReply, queryOf } from './sales-crm-mock-response.utils'
import { salesMockAdapter } from './sales-mock-adapter'
import { findSalesUserById } from './sales-roster-mock-store'
import { listTargets } from './targets-mock-store'
import { findTenantById } from './tenants-mock-store'

import type { MockReply } from './sales-crm-mock-response.utils'
import type { TargetWithOverdue } from './targets-mock-store'
import type {
  MyPageDisplayStatus,
  MyPagePaymentState,
  MyPagePendingReassignment,
  MyPagePerson,
  MyPagePriority,
  MyPageStage,
  MyPageStageCode,
  MyPageSummary,
  MyPageTargetAction,
  MyPageTargetContact,
  MyPageTargetDetails,
  MyPageTargetEvent,
  MyPageTargetRow,
  MyPageTargetStatus,
} from '@/features/sales/types/my-page.types'
import type { SalesStage, SalesUserRecord, Tenant } from '@/features/sales/types/tenant.types'
import type { AxiosRequestConfig } from 'axios'

/** @description A target together with the bank it is for. */
export interface TargetSource {
  target: TargetWithOverdue
  tenant: Tenant
}

/** @description The backend's stage code and label for each of the frontend's stages. */
const STAGES: Record<SalesStage, MyPageStage> = {
  Prospect: { code: 'NEW', label: 'New' },
  Contacted: { code: 'CONTACTED', label: 'Contacted' },
  'Meeting/Demo': { code: 'DEMO_REQUESTED', label: 'Demo Requested' },
  Proposal: { code: 'PROPOSAL_SENT', label: 'Proposal Sent' },
  Negotiation: { code: 'UNDER_NEGOTIATION', label: 'Under Negotiation' },
  'Awaiting Payment': { code: 'SUBSCRIPTION_REQUESTED', label: 'Subscription Requested' },
  'Active Client': { code: 'LIVE', label: 'Live' },
  'On Hold': { code: 'ON_HOLD', label: 'On Hold' },
  Lost: { code: 'LOST', label: 'Lost' },
}

/** @description The stages an open target can be At Risk in (M4); later stages never are. */
const AT_RISK_STAGES: readonly MyPageStageCode[] = [
  'NEW',
  'CONTACTED',
  'DEMO_REQUESTED',
  'PROPOSAL_SENT',
]

/** @description The share of a period that may remain before an early-stage target is At Risk. */
const AT_RISK_REMAINING_PERCENT = 20

const PRIORITIES: Record<TargetWithOverdue['priority'], MyPagePriority> = {
  High: 'HIGH',
  Medium: 'MEDIUM',
  Low: 'LOW',
}

const PRIORITY_RANK: Record<MyPagePriority, number> = { HIGH: 0, MEDIUM: 1, LOW: 2 }

const PERIOD_TYPES = {
  Monthly: 'MONTHLY',
  Quarterly: 'QUARTERLY',
  'Half-Yearly': 'BI_ANNUAL',
  Annual: 'ANNUAL',
} as const

const POSITION_CODES: Record<string, string> = {
  'Sales Head': 'SALES_HEAD',
  'Sales Manager': 'SALES_MANAGER',
  'Sales Executive': 'SALES_EXECUTIVE',
}

/**
 * @description Today's calendar date in India, the zone every target status is judged in.
 * @param {Date} [now] - The reference time (defaults to the current time; overridable for tests).
 * @returns {string} The date as yyyy-mm-dd.
 */
export function indiaToday(now = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now)
}

/**
 * @description The number of whole days from the epoch to a yyyy-mm-dd date, so two dates can be
 * subtracted without time-zone drift.
 * @param {string} isoDate - The date.
 * @returns {number} Its day number.
 */
function dayNumber(isoDate: string): number {
  return Math.floor(Date.parse(`${isoDate}T00:00:00Z`) / 86_400_000)
}

/**
 * @description The April to March financial year a date falls in, as the My Page summary reports it.
 * @param {string} isoDate - The date, yyyy-mm-dd.
 * @returns {{ label: string; start: string; end: string }} The year's label ("2026-27") and its
 * first and last day.
 */
export function financialYearOf(isoDate: string): { label: string; start: string; end: string } {
  const year = Number(isoDate.slice(0, 4))
  const startYear = Number(isoDate.slice(5, 7)) >= 4 ? year : year - 1
  return {
    label: `${String(startYear)}-${String(startYear + 1).slice(-2)}`,
    start: `${String(startYear)}-04-01`,
    end: `${String(startYear + 1)}-03-31`,
  }
}

/**
 * @description A target's stored status: Completed once payment is confirmed, Closed once Lost,
 * otherwise Open. (The mock keeps a Lost target outstanding; the backend closes it, M2.)
 * @param {TargetWithOverdue} target - The target.
 * @returns {MyPageTargetStatus} Its stored status.
 */
export function targetStatusOf(target: TargetWithOverdue): MyPageTargetStatus {
  if (target.status === 'Completed') return 'COMPLETED'
  return target.stage === 'Lost' ? 'CLOSED' : 'OPEN'
}

/**
 * @description A target's payment state: confirmed once completed; while Awaiting Payment, sent
 * unless a command cleared it (a seeded Awaiting Payment target counts as sent); otherwise not
 * sent.
 * @param {TargetWithOverdue} target - The target.
 * @returns {MyPagePaymentState} Its payment state.
 */
export function paymentStateOf(target: TargetWithOverdue): MyPagePaymentState {
  if (target.status === 'Completed') return 'CONFIRMED'
  if (target.stage !== 'Awaiting Payment') return 'NOT_SENT'
  return recordedPaymentSentAt(target.id) === null ? 'NOT_SENT' : 'SENT'
}

/**
 * @description Derives a target's display status, first match wins (M4): Completed, Closed,
 * Overdue (open and the period has ended, whatever the stage), Awaiting Payment, At Risk (an early
 * stage with at most a fifth of the period left), otherwise On Track.
 * @param {TargetWithOverdue} target - The target.
 * @param {string} today - Today in India, yyyy-mm-dd.
 * @returns {MyPageDisplayStatus} Its display status.
 */
export function displayStatusOf(target: TargetWithOverdue, today: string): MyPageDisplayStatus {
  const status = targetStatusOf(target)
  if (status === 'COMPLETED') return 'COMPLETED'
  if (status === 'CLOSED') return 'CLOSED'
  if (target.endDate < today) return 'OVERDUE'
  if (paymentStateOf(target) === 'SENT') return 'AWAITING_PAYMENT'
  const length = dayNumber(target.endDate) - dayNumber(target.startDate) + 1
  const remaining = dayNumber(target.endDate) - dayNumber(today)
  const early = AT_RISK_STAGES.includes(STAGES[target.stage].code)
  return early && remaining <= (length * AT_RISK_REMAINING_PERCENT) / 100 ? 'AT_RISK' : 'ON_TRACK'
}

/**
 * @description A roster user as a person named on a target.
 * @param {string} userId - The user's roster id.
 * @returns {MyPagePerson} The person.
 */
export function personOf(userId: string): MyPagePerson {
  const user = findSalesUserById(userId)
  return {
    userId,
    displayName: user?.name ?? userId,
    employeeCode: userId,
    positionCode: user ? (POSITION_CODES[user.role] ?? null) : null,
  }
}

/** @description One handover of a bank from one user to another (not its first assignment). */
type Handover = Omit<Tenant['ownerHistory'][number], 'fromOwnerId'> & { fromOwnerId: string }

/**
 * @description A bank's handovers, oldest first. The bank's first assignment (no previous owner)
 * isn't one: the backend only records a reassignment when the target moves between people.
 * @param {Tenant} tenant - The bank.
 * @returns {Handover[]} Its handovers.
 */
function handoversOf(tenant: Tenant): Handover[] {
  return tenant.ownerHistory.flatMap((transfer) =>
    transfer.fromOwnerId === null ? [] : [{ ...transfer, fromOwnerId: transfer.fromOwnerId }],
  )
}

/**
 * @description The latest handover that made this target's owner its assignee, if there was one.
 * @param {TargetSource} source - The target and its bank.
 * @returns {Handover | undefined} The handover.
 */
function lastTransferTo(source: TargetSource): Handover | undefined {
  return handoversOf(source.tenant)
    .reverse()
    .find((transfer) => transfer.toOwnerId === source.target.ownerId)
}

/**
 * @description A target's open reassignment request as the row shows it.
 * @param {string} targetId - The target.
 * @returns {MyPagePendingReassignment | null} The request, or null when none is open.
 */
function pendingOf(targetId: string): MyPagePendingReassignment | null {
  const pending = pendingReassignmentOf(targetId)
  if (!pending) return null
  return {
    approvalRequestId: pending.approvalRequestId,
    proposedAssignee: personOf(pending.proposedAssigneeId),
    requestedBy: personOf(pending.requestedById),
    requestedAt: pending.requestedAt,
  }
}

/**
 * @description The actions the caller may take on a target right now, by the backend's rules: only
 * an open target changes; its assignee or a Super Admin updates the stage; a Super Admin, Head or
 * Manager reassigns (an Awaiting Payment target only a Super Admin) while an Executive asks
 * instead; payment details go out from the Subscription Requested stage; only a Super Admin
 * confirms a sent payment.
 * @param {TargetSource} source - The target and its bank.
 * @param {SalesUserRecord | undefined} caller - The signed-in caller.
 * @returns {MyPageTargetAction[]} The actions on offer.
 */
function allowedActionsOf(
  source: TargetSource,
  caller: SalesUserRecord | undefined,
): MyPageTargetAction[] {
  const { target } = source
  if (!caller || targetStatusOf(target) !== 'OPEN') return []
  const isSuperAdmin = caller.role === 'Super Admin'
  const isAssignee = target.ownerId === caller.id
  const awaitingPayment = target.stage === 'Awaiting Payment'
  const paymentSent = paymentStateOf(target) === 'SENT'
  const pending = pendingReassignmentOf(target.id) !== undefined
  const actions: MyPageTargetAction[] = []
  if (isSuperAdmin || isAssignee) actions.push('UPDATE_STAGE')
  if ((isSuperAdmin || isAssignee) && awaitingPayment && paymentSent) actions.push('REOPEN')
  if (!pending && caller.role !== 'Sales Executive' && (!paymentSent || isSuperAdmin)) {
    actions.push('REASSIGN')
  }
  if (!pending && caller.role === 'Sales Executive' && isAssignee && !paymentSent) {
    actions.push('REQUEST_REASSIGNMENT')
  }
  if (awaitingPayment) actions.push('SEND_PAYMENT_DETAILS')
  if (isSuperAdmin && paymentSent) actions.push('CONFIRM_PAYMENT')
  return actions
}

/**
 * @description Projects a target and its bank as a My Page row.
 * @param {TargetSource} source - The target and its bank.
 * @param {string} today - Today in India, yyyy-mm-dd.
 * @returns {MyPageTargetRow} The row.
 */
export function toMyPageRow(source: TargetSource, today: string): MyPageTargetRow {
  const { target, tenant } = source
  const transfer = lastTransferTo(source)
  return {
    targetId: target.id,
    prospectId: tenant.id,
    bankName: tenant.name,
    tenantCode: tenant.id,
    city: tenant.city || null,
    state: tenant.state || null,
    stage: STAGES[target.stage],
    targetStatus: targetStatusOf(target),
    displayStatus: displayStatusOf(target, today),
    priority: PRIORITIES[target.priority],
    name: target.name,
    periodType: PERIOD_TYPES[target.periodType],
    periodLabel: target.periodLabel,
    periodStart: target.startDate,
    dueDate: target.endDate,
    assignee: personOf(target.ownerId),
    reassignedFrom: transfer ? personOf(transfer.fromOwnerId) : null,
    pendingReassignment: pendingOf(target.id),
    paymentState: paymentStateOf(target),
    paymentSentAt: recordedPaymentSentAt(target.id) ?? null,
    allowedActions: allowedActionsOf(source, mockCaller()),
    version: `tg1.${target.id}.${String(revisionOf(target.id))}`,
  }
}

/**
 * @description The bank's authorised contact as the target's primary contact, or null when none
 * was recorded.
 * @param {Tenant} tenant - The bank.
 * @returns {MyPageTargetContact | null} The contact.
 */
function primaryContactOf(tenant: Tenant): MyPageTargetContact | null {
  const contact = tenant.authorisedContact
  if (!contact.name) return null
  return {
    name: contact.name,
    designation: contact.designation || null,
    email: contact.email || null,
    phone: contact.phone || null,
  }
}

/**
 * @description A target's history, oldest first: each handover of its bank, the entries the My
 * Page commands logged (stage changes, reopenings, payment sends) and, once completed, the payment
 * confirmation.
 * @param {TargetSource} source - The target and its bank.
 * @returns {MyPageTargetEvent[]} The events.
 */
function eventsOf(source: TargetSource): MyPageTargetEvent[] {
  const { target, tenant } = source
  const events: MyPageTargetEvent[] = handoversOf(tenant).map((transfer, index) => ({
    eventId: `${target.id}-transfer-${String(index + 1)}`,
    eventType: 'REASSIGNED',
    fromStage: null,
    toStage: null,
    fromUser: personOf(transfer.fromOwnerId),
    toUser: personOf(transfer.toOwnerId),
    actor: personOf(transfer.actingUserId),
    reason: null,
    recipientKind: null,
    createdAt: transfer.transferredAt,
  }))
  if (target.completedAt && target.confirmedByUserId) {
    events.push({
      eventId: `${target.id}-payment-confirmed`,
      eventType: 'PAYMENT_CONFIRMED',
      fromStage: null,
      toStage: 'LIVE',
      fromUser: null,
      toUser: null,
      actor: personOf(target.confirmedByUserId),
      reason: null,
      recipientKind: null,
      createdAt: target.completedAt,
    })
  }
  events.push(...loggedEventsOf(target.id))
  return events.sort((a, b) => a.createdAt.localeCompare(b.createdAt))
}

/**
 * @description Projects a target and its bank as My Page details.
 * @param {TargetSource} source - The target and its bank.
 * @param {string} today - Today in India, yyyy-mm-dd.
 * @returns {MyPageTargetDetails} The details.
 */
export function toMyPageDetails(source: TargetSource, today: string): MyPageTargetDetails {
  const { target, tenant } = source
  const transfer = lastTransferTo(source)
  return {
    ...toMyPageRow(source, today),
    reassignedAt: transfer?.transferredAt ?? null,
    closedAt: null,
    completedAt: target.completedAt,
    paymentConfirmedAt: target.completedAt,
    contacts: { primary: primaryContactOf(tenant), secondary: null },
    events: eventsOf(source),
  }
}

/**
 * @description Every target the caller may see, each with its bank: all of them for a Super Admin,
 * only the caller's own for anyone else (M6), and none when the caller isn't on the roster.
 * @param {SalesUserRecord | undefined} caller - The signed-in caller.
 * @returns {TargetSource[]} The visible targets, in the store's order.
 */
export function visibleTo(caller: SalesUserRecord | undefined): TargetSource[] {
  if (!caller) return []
  return listTargets()
    .filter((target) => caller.role === 'Super Admin' || target.ownerId === caller.id)
    .flatMap((target) => {
      const tenant = findTenantById(target.tenantId)
      return tenant ? [{ target, tenant }] : []
    })
}

/**
 * @description Whether a row's bank, tenant code, city or state contains the search text,
 * ignoring case.
 * @param {MyPageTargetRow} row - The row.
 * @param {string | undefined} search - The search text, if any.
 * @returns {boolean} True when there is no search or the row matches it.
 */
function matchesSearch(row: MyPageTargetRow, search: string | undefined): boolean {
  const needle = search?.trim().toLowerCase()
  if (!needle) return true
  return [row.bankName, row.tenantCode, row.city, row.state].some((value) =>
    value?.toLowerCase().includes(needle),
  )
}

/**
 * @description Whether a row matches the list's filters.
 * @param {MyPageTargetRow} row - The row.
 * @param {Record<string, string | undefined>} query - The list's query parameters.
 * @returns {boolean} True when it does.
 */
function matchesFilters(row: MyPageTargetRow, query: Record<string, string | undefined>): boolean {
  const checks: [string | undefined, string][] = [
    [query.stage, row.stage.code],
    [query.status, row.displayStatus],
    [query.priority, row.priority],
    [query.assigneeUserId, row.assignee.userId],
  ]
  return (
    matchesSearch(row, query.search) &&
    checks.every(([wanted, actual]) => !wanted || wanted === actual)
  )
}

/**
 * @description Answers List My Page Targets: the visible open targets and, unless
 * `includeClosed=false`, the closed ones of the current financial year, filtered and sorted by due
 * date, then priority, then bank name; completed targets are never listed.
 * @param {AxiosRequestConfig} config - The request config, for the query.
 * @returns {MockReply} The page.
 */
export function myPageListReply(config: AxiosRequestConfig): MockReply {
  const query = queryOf(config)
  const today = indiaToday()
  const yearStart = financialYearOf(today).start
  const includeClosed = query.includeClosed !== 'false'
  const rows = visibleTo(mockCaller())
    .map((source) => toMyPageRow(source, today))
    .filter((row) => {
      if (row.targetStatus === 'COMPLETED') return false
      if (row.targetStatus === 'CLOSED') return includeClosed && row.dueDate >= yearStart
      return true
    })
    .filter((row) => matchesFilters(row, query))
    .sort(
      (a, b) =>
        a.dueDate.localeCompare(b.dueDate) ||
        PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority] ||
        a.bankName.localeCompare(b.bankName),
    )
  return pageReply(config, rows)
}

/**
 * @description Answers Get My Page Summary: the visible targets in the financial-year window (plus
 * any older target still open), each counted once under its display status.
 * @param {AxiosRequestConfig} config - The request config, for the optional assignee.
 * @returns {MockReply} The counts.
 */
export function summaryReply(config: AxiosRequestConfig): MockReply {
  const { assigneeUserId } = queryOf(config)
  const today = indiaToday()
  const year = financialYearOf(today)
  const counts: Record<MyPageDisplayStatus, number> = {
    COMPLETED: 0,
    CLOSED: 0,
    OVERDUE: 0,
    AWAITING_PAYMENT: 0,
    AT_RISK: 0,
    ON_TRACK: 0,
  }
  for (const source of visibleTo(mockCaller())) {
    const { target } = source
    if (assigneeUserId && target.ownerId !== assigneeUserId) continue
    const open = targetStatusOf(target) === 'OPEN'
    if (!open && target.endDate < year.start) continue
    counts[displayStatusOf(target, today)] += 1
  }
  const summary: MyPageSummary = {
    assigneeUserId: assigneeUserId ?? null,
    financialYear: year.label,
    windowStart: year.start,
    windowEnd: year.end,
    total: Object.values(counts).reduce((sum, count) => sum + count, 0),
    onTrack: counts.ON_TRACK,
    atRisk: counts.AT_RISK,
    overdue: counts.OVERDUE,
    awaitingPayment: counts.AWAITING_PAYMENT,
    completed: counts.COMPLETED,
    closed: counts.CLOSED,
  }
  return [200, okEnvelope(summary, { evaluatedAt: new Date().toISOString() })]
}

/**
 * @description Answers Get Target Details: one visible target in full, or 404/4113 when it doesn't
 * exist or lies outside the caller's visibility (the backend answers both the same way).
 * @param {AxiosRequestConfig} config - The request config, for the target id in its URL.
 * @returns {MockReply} The details or the refusal.
 */
export function myPageDetailsReply(config: AxiosRequestConfig): MockReply {
  const targetId = decodeURIComponent(config.url?.split('/').at(-1) ?? '')
  const source = visibleTo(mockCaller()).find((candidate) => candidate.target.id === targetId)
  if (!source) return failReply(404, '4113', 'Target not found')
  return [200, okEnvelope(toMyPageDetails(source, indiaToday()))]
}

if (salesMockAdapter) {
  salesMockAdapter.onGet('/sales/targets/summary').reply(summaryReply)
}
